import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { isCaptured, markCaptured, markSampled } from "@/lib/visitor";
import { toast } from "sonner";
import { Check, ShieldCheck, FileText, Star, Mail, Headphones, Book } from "lucide-react";
import SEO from "@/components/SEO";
import AudioPlayer from "@/components/AudioPlayer";

const FMT = {
  ebook: { key: "ebook", label: "eBook (PDF)", tagline: "Instant download" },
  audio: { key: "audio", label: "Audiobook", tagline: "Stream or download MP3" },
  bundle: { key: "bundle", label: "Bundle", tagline: "eBook + Audiobook" },
};

export default function EbookDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [format, setFormat] = useState("ebook");
  // Unified sample-unlock gate: kind is 'pdf' or 'audio' — controls the modal copy/icon accent
  const [gateKind, setGateKind] = useState(null);   // null = closed, "pdf" | "audio" = open
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const audioAutoplayRef = useRef(false);

  useEffect(() => {
    api.get(`/ebooks/${slug}`).then((r) => setB(r.data)).catch(() => setB(false));
  }, [slug]);

  const prices = useMemo(() => {
    if (!b) return { ebook: 0, audio: 0, bundle: 0, bundleFullSum: 0 };
    const ebook = Number(b.price || 0);
    const audio = Number(b.audio_price || 0);
    const pct = Number(b.bundle_discount_pct ?? 25);
    const bundleFullSum = ebook + audio;
    const bundle = Math.round(bundleFullSum * (1 - pct / 100) * 100) / 100;
    return { ebook, audio, bundle, bundleFullSum, pct };
  }, [b]);

  if (b === null) return <div className="max-w-7xl mx-auto px-6 py-24 text-navy/50">Loading…</div>;
  if (b === false) return <div className="max-w-7xl mx-auto px-6 py-24 text-navy/50">Ebook not found.</div>;

  const hasAudio = !!b.has_audio && Number(b.audio_price || 0) > 0;
  const activePrice = format === "audio" ? prices.audio : format === "bundle" ? prices.bundle : prices.ebook;

  const addToCart = () => { add(b, format, activePrice); toast.success("Added to cart."); };
  const buyNow = () => { add(b, format, activePrice); nav("/cart"); };

  // Sample gate is bypassed when the user is logged in (fresh device / mobile fix)
  // OR when their email was previously captured on this device.
  const isUnlocked = () => !!user || isCaptured();

  const openPreview = () => {
    if (isUnlocked()) {
      markSampled();
      window.open(`${API}/ebooks/${b.slug}/preview`, "_blank");
      return;
    }
    setGateKind("pdf");
  };

  const openAudioSample = () => {
    if (isUnlocked()) {
      markSampled();
      setAudioUnlocked(true);
      return;
    }
    audioAutoplayRef.current = true;
    setGateKind("audio");
  };

  const unlockSample = async (e) => {
    e.preventDefault();
    if (!previewEmail) return;
    setPreviewBusy(true);
    try {
      await api.post("/subscribe", { email: previewEmail, source: `${gateKind === "audio" ? "sample_audio" : "sample_pdf"}:${b.slug}` });
      markCaptured();
      markSampled();
      toast.success("Sample sent to your inbox — opening it now too.");
      if (gateKind === "audio") {
        setAudioUnlocked(true);
      } else {
        window.open(`${API}/ebooks/${b.slug}/preview`, "_blank");
      }
      setGateKind(null);
      setPreviewEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
    setPreviewBusy(false);
  };

  const audioPreviewUrl = b.audio_preview_storage_path ? `${API}/media/${b.audio_preview_storage_path}` : null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="ebook-detail-page">
      <SEO
        title={b.title}
        description={b.description}
        canonical={`https://apertureent.com/ebook/${b.slug}`}
        image={b.cover_image_url}
        product={{ title: b.title, description: b.description, image: b.cover_image_url, category: b.category, price: b.price }}
      />
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="aspect-[3/4] bg-[#F7F5F0] overflow-hidden shadow-2xl rounded-sm flex items-center justify-center p-6">
            <img src={b.cover_image_url} alt={b.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
          </div>
          {b.has_preview && (
            <button onClick={openPreview} data-testid="ebook-preview-btn"
              className="mt-6 w-full inline-flex justify-center items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white transition-colors duration-300 py-3 text-xs uppercase tracking-widest rounded-sm">
              <FileText className="w-4 h-4" strokeWidth={1.5} /> Preview Sample Pages
            </button>
          )}
          {hasAudio && audioPreviewUrl && (
            <div className="mt-6 p-4 bg-white border rounded-sm" style={{ borderColor: "rgba(10,37,64,0.10)" }} data-testid="audio-preview-block">
              {audioUnlocked || isUnlocked() ? (
                <>
                  <AudioPlayer src={audioPreviewUrl} label="60-Second Audio Sample" testid="audio-preview-player" />
                  {b.narrator && <div className="mt-3 text-xs text-navy/60">Narrated by <span className="text-navy">{b.narrator}</span></div>}
                </>
              ) : (
                <>
                  <button onClick={openAudioSample} data-testid="audio-sample-unlock-btn"
                    className="w-full inline-flex justify-center items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white transition-colors duration-300 py-3 text-xs uppercase tracking-widest rounded-sm">
                    <Headphones className="w-4 h-4" strokeWidth={1.5}/> Unlock 60-second audio sample
                  </button>
                  {b.narrator && <div className="mt-3 text-xs text-navy/60 text-center">Narrated by <span className="text-navy">{b.narrator}</span></div>}
                </>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="eyebrow mb-4">{b.category}</div>
          <h1 className="aperture-h1 text-4xl md:text-5xl leading-tight">{b.title}</h1>
          <div className="mt-2 text-sm text-navy/60">by {b.author}{b.narrator && hasAudio && <> · Narrated by <span className="text-navy">{b.narrator}</span></>}</div>
          <div className="mt-8 gold-hairline" />
          <p className="mt-8 text-lg text-slate-700 leading-relaxed font-sans">{b.description}</p>
          {b.long_description && <p className="mt-4 text-slate-600 leading-relaxed">{b.long_description}</p>}

          {/* Format selector */}
          <div className="mt-10">
            <div className="eyebrow mb-3">Choose Your Format</div>
            <div className={`grid ${hasAudio ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1"} gap-3`} data-testid="format-selector">
              {/* eBook */}
              <button type="button" onClick={() => setFormat("ebook")} data-testid="format-ebook"
                className={`text-left p-5 rounded-sm border-2 transition-all ${format === "ebook" ? "border-gold bg-white shadow-md" : "border-navy/10 bg-white/60 hover:border-navy/30"}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-navy/60"><FileText className="w-3 h-3"/>{FMT.ebook.label}</div>
                <div className="mt-3 font-serif text-2xl text-navy">${prices.ebook.toFixed(2)}</div>
                <div className="mt-1 text-xs text-navy/50">{FMT.ebook.tagline}</div>
              </button>
              {/* Audio */}
              {hasAudio && (
                <button type="button" onClick={() => setFormat("audio")} data-testid="format-audio"
                  className={`text-left p-5 rounded-sm border-2 transition-all ${format === "audio" ? "border-gold bg-white shadow-md" : "border-navy/10 bg-white/60 hover:border-navy/30"}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-navy/60"><Headphones className="w-3 h-3"/>{FMT.audio.label}</div>
                  <div className="mt-3 font-serif text-2xl text-navy">${prices.audio.toFixed(2)}</div>
                  <div className="mt-1 text-xs text-navy/50">{FMT.audio.tagline}</div>
                </button>
              )}
              {/* Bundle */}
              {hasAudio && (
                <button type="button" onClick={() => setFormat("bundle")} data-testid="format-bundle"
                  className={`relative text-left p-5 rounded-sm border-2 transition-all ${format === "bundle" ? "border-gold bg-white shadow-md" : "border-navy/10 bg-white/60 hover:border-navy/30"}`}>
                  <span className="absolute top-2 right-2 text-[9px] tracking-widest uppercase text-gold">Save {Math.round(prices.pct)}%</span>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-navy/60">Bundle · Best value</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-serif text-2xl text-navy">${prices.bundle.toFixed(2)}</span>
                    <span className="text-xs text-navy/40 line-through">${prices.bundleFullSum.toFixed(2)}</span>
                  </div>
                  <div className="mt-1 text-xs text-navy/50">eBook + Audiobook</div>
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-navy-10 pt-6" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
            <div>
              <div className="eyebrow">Selected</div>
              <div className="font-serif text-4xl text-navy mt-1" data-testid="ebook-price">${activePrice.toFixed(2)}</div>
            </div>
            <div className="flex gap-3">
              <button onClick={addToCart} data-testid="ebook-add-cart-btn" className="px-6 py-3.5 border border-navy text-navy hover:bg-navy hover:text-white text-xs uppercase tracking-widest transition-colors duration-300 rounded-sm">Add to Cart</button>
              <button onClick={buyNow} data-testid="ebook-buy-now-btn" className="btn-primary px-8 py-3.5 text-xs uppercase tracking-widest rounded-sm">Buy Now</button>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-navy/60 uppercase tracking-widest">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" strokeWidth={1.5}/> Instant Access</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" strokeWidth={1.5}/> Lifetime Ownership</div>
            {hasAudio && <div className="flex items-center gap-2"><Headphones className="w-4 h-4 text-gold" strokeWidth={1.5}/> Stream or Download</div>}
          </div>

          {b.table_of_contents?.length > 0 && (
            <div className="mt-14">
              <div className="eyebrow mb-4">Table of Contents</div>
              <ol className="space-y-3">
                {b.table_of_contents.map((t, i) => (
                  <li key={i} data-testid={`toc-${i}`} className="flex gap-4 items-start">
                    <span className="font-serif text-gold text-lg leading-none w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-slate-700">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {b.testimonials?.length > 0 && (
            <div className="mt-14">
              <div className="eyebrow mb-4">Reader Notes</div>
              <div className="space-y-6">
                {b.testimonials.map((t, i) => (
                  <blockquote key={i} data-testid={`testimonial-${i}`} className="border-l-2 border-gold pl-6 py-2">
                    <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 fill-gold text-gold" />)}</div>
                    <p className="font-serif text-lg text-navy italic">"{t.quote}"</p>
                    <div className="mt-2 text-sm text-navy/60">— {t.name}</div>
                  </blockquote>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {gateKind && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-6 z-50" data-testid="sample-gate-modal" onClick={()=>setGateKind(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8">
            <div className="flex items-center gap-2 mb-5">
              <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${gateKind === "pdf" ? "border-gold" : "border-navy/20"}`}>
                <Book className={`w-5 h-5 ${gateKind === "pdf" ? "text-gold" : "text-navy/30"}`} strokeWidth={1.5} />
              </div>
              <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${gateKind === "audio" ? "border-gold" : "border-navy/20"}`}>
                <Headphones className={`w-5 h-5 ${gateKind === "audio" ? "text-gold" : "text-navy/30"}`} strokeWidth={1.5} />
              </div>
            </div>
            <div className="eyebrow mb-2">{gateKind === "audio" ? "Unlock the audio sample" : "Free Sample"}</div>
            <h2 className="aperture-h2 text-2xl leading-tight mb-3" data-testid="sample-gate-title">
              {gateKind === "audio" ? "Hear a 60-second taste — on the house." : "Sent to your inbox — opening now too."}
            </h2>
            <p className="text-sm text-navy/70 leading-relaxed mb-6">
              {gateKind === "audio"
                ? <>Leave your email and we'll unlock a 60-second sample of <span className="text-navy font-medium">{b.title}</span> right here — plus send you the sample pages to read anytime. We'll drop you a short note when new titles launch. No noise, no pressure.</>
                : <>A 6-page sample from <span className="text-navy font-medium">{b.title}</span> will land in your inbox as a PDF, and we'll open it in a new tab right now. We may also send you launch notes on future titles — unsubscribe any time.</>
              }
            </p>
            <form onSubmit={unlockSample} className="space-y-3" data-testid="sample-gate-form">
              <input required type="email" value={previewEmail} onChange={(e)=>setPreviewEmail(e.target.value)}
                placeholder="your@email.com" autoFocus data-testid="sample-gate-email"
                className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              <button disabled={previewBusy} data-testid="sample-gate-submit" className="btn-primary w-full py-3 text-xs uppercase tracking-widest inline-flex justify-center items-center gap-2 rounded-sm">
                {gateKind === "audio" ? <Headphones className="w-3.5 h-3.5"/> : <Mail className="w-3.5 h-3.5"/>}
                {previewBusy ? "Unlocking…" : "Send me the sample"}
              </button>
              <button type="button" onClick={()=>setGateKind(null)} data-testid="sample-gate-cancel" className="w-full pt-1 text-[11px] uppercase tracking-widest text-navy/40 hover:text-navy transition-colors">
                Not now
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
